import type { Workbook } from 'exceljs';
import { DECIMAL, DataDownloadFile, DataExport, cellValue, fileBaseName } from './data-export';
import { datamapRows, decipherBlocks, withoutCounters } from './decipher-datamap';

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Decipher's Excel stores any cell that is plainly a number as one, whatever its question's type --
 * a section timing, a DMA code, a scale point titled "1". Only where the number reads back as the
 * same text, though, so an identifier like 02134 or a 1.50 someone typed stays as written.
 */
function excelCell(value: string | number | null): string | number | null {
  return typeof value === 'string' && DECIMAL.test(value) && String(Number(value)) === value ? Number(value) : value;
}

/**
 * Decipher's Excel download: the responses on one sheet and the datamap on a sheet called Datamap,
 * which is what a Decipher Excel upload asks for back. Decipher names the data sheet after the
 * survey's table; the upload finds it by elimination, so Data says plainly what it holds. Like
 * Decipher's, it leaves out the quota counters its CSV carries.
 */
export async function decipherWorkbook(download: DataExport): Promise<Workbook> {
  const data = withoutCounters(download);
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();

  const sheet = workbook.addWorksheet('Data');
  sheet.addRow(data.columns);
  for (const record of data.records) {
    sheet.addRow(data.columns.map(column => excelCell(cellValue(data, record, column))));
  }

  // The workbook's datamap drops the trailing spaces datamap.txt keeps after a question's wording.
  const datamap = workbook.addWorksheet('Datamap');
  for (const row of datamapRows(decipherBlocks(data))) {
    datamap.addRow(row.map(cell => {
      const value = typeof cell === 'string' ? cell.trimEnd() : cell;
      return value === '' ? null : excelCell(value);
    }));
  }

  return workbook;
}

export async function decipherExcelDownload(data: DataExport, reportName: string): Promise<DataDownloadFile> {
  const buffer = await (await decipherWorkbook(data)).xlsx.writeBuffer();
  return { file: new Blob([buffer], { type: XLSX_TYPE }), fileName: `${fileBaseName(reportName)} data.xlsx` };
}
